// AppPage.jsx  ->  /app
//
// Η σελίδα της εφαρμογής. Τέσσερις ενότητες, τέσσερις διαφορετικές διατάξεις:
//   1. hero          κείμενο + συσκευή, ασύμμετρο
//   2. τι κάνει      πλέγμα 2 στηλών με άνισα κελιά, ένα από αυτά ζωντανό
//   3. πότε μιλάει   κάθετη ράγα με τα ορόσημα, χωρίς κάρτες
//   4. τι δεν κάνει  ένα πλάτος, μόνο κείμενο
// Καμία διάταξη δεν επαναλαμβάνεται, και καμία δεν είναι ίδια με την ενότητα
// της αρχικής.

import { Link } from 'react-router-dom'
import { FaApple, FaGooglePlay } from 'react-icons/fa6'
import Header from './Header.jsx'
import Footer from './Footer.jsx'
import DocumentMeta from './DocumentMeta.jsx'
import { MAX_YEAR } from './constants.js'
import { NotificationPreview, PhonePreview } from './appPreview.jsx'
import { useCountdown } from './appCountdown.js'

/** Τα ορόσημα, όπως ακριβώς τα στέλνει η εφαρμογή. */
const MILESTONES = [
  { days: 100, title: '100 μέρες', body: 'Απομένουν 100 μέρες για το πρώτο σου μάθημα.' },
  { days: 50, title: '50 μέρες', body: 'Απομένουν 50 μέρες για το πρώτο σου μάθημα.' },
  { days: 30, title: '30 μέρες', body: 'Απομένουν 30 μέρες για το πρώτο σου μάθημα.' },
  { days: 7, title: '7 μέρες', body: 'Απομένουν 7 μέρες για το πρώτο σου μάθημα.' },
  { days: 1, title: 'Αύριο: Νεοελληνική', body: 'Παρασκευή 28 Μαΐου, στις 08:30. Καλή επιτυχία.' },
]

export default function AppPage() {
  const { days, progress } = useCountdown()

  return (
    <>
      <DocumentMeta
        title="Panellele | Πανελλήνιες+"
        description="Αντίστροφη μέτρηση για τις Πανελλαδικές, ειδοποιήσεις πριν από κάθε εξέταση και βάσεις εισαγωγής, στο κινητό σου. Σύντομα για iPhone και Android."
        canonical="https://www.panellhniesplus.gr/app"
      />

      <div className="min-h-screen bg-white text-gray-900">
        <Header />

        {/* 1. HERO */}
        <section className="border-b border-gray-100">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-7">
                <p className="text-sm font-medium text-gray-500">Σύντομα</p>

                <h1 className="mt-2 text-3xl sm:text-5xl font-semibold tracking-tight leading-[1.12]">
                  Πόσο έμεινε, στο κινητό σου.
                </h1>

                <p className="mt-4 text-base sm:text-lg text-gray-600 max-w-2xl">
                 Το Panellele σου λέει πόσο έμεινε για κάθε μάθημα που δίνεις, σου το θυμίζει πριν
                  φτάσει, και έχει μαζί όλες τις βάσεις. Χωρίς λογαριασμό.
                </p>

                <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-gray-500">
                  <span className="inline-flex items-center gap-2">
                    <FaApple className="h-4 w-4" aria-hidden="true" />
                    iPhone
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <FaGooglePlay className="h-3.5 w-3.5" aria-hidden="true" />
                    Android
                  </span>
                  <span className="text-gray-500">Ετοιμάζεται</span>
                </div>
              </div>

              <div className="lg:col-span-5">
                {/* Μικρότερη συσκευή εδώ: με το κανονικό μέγεθος η στήλη του
                    κειμένου κεντράρεται πολύ χαμηλά και μοιάζει με λάθος. */}
                <div className="mx-auto w-full max-w-[262px]">
                  <PhonePreview days={days} progress={progress} size={148} />
                  <p className="mt-4 text-center text-xs text-gray-500">
                    Ζωντανή μέτρηση. Η ημερομηνία είναι εκτίμηση μέχρι να βγει το ΦΕΚ.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 2. ΤΙ ΚΑΝΕΙ: πλέγμα με άνισα κελιά, ένα από αυτά ζωντανό */}
        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">Τι κάνει</h2>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Πλατύ κελί: η ειδοποίηση, σε πραγματικό μέγεθος */}
              <div className="md:col-span-2 rounded-2xl border border-gray-200 p-6">
                <p className="text-sm font-medium text-gray-900">
                  Σου θυμίζει πριν από κάθε εξέταση
                </p>
                <p className="mt-1 text-sm text-gray-600 max-w-md">
                  Το απόγευμα πριν από κάθε μάθημα που δίνεις. Αυτό ακριβώς φτάνει στο κινητό:
                </p>

                <div className="mt-5 max-w-sm">
                  <NotificationPreview
                    title="Αύριο: Νεοελληνική"
                    body="Παρασκευή 28 Μαΐου, στις 08:30. Καλή επιτυχία."
                    when="18:00"
                  />
                </div>
              </div>

              <div className="flex flex-col justify-center rounded-2xl border border-gray-200 p-6">
                <p className="text-sm font-medium text-gray-900">Μόνο τα δικά σου μαθήματα</p>
                <p className="mt-1 text-sm text-gray-600">
                  Διαλέγεις πεδίο και βλέπεις τη δική σου σειρά, με τα ειδικά μαθήματα αν δίνεις.
                </p>
              </div>

              <div className="flex flex-col justify-center rounded-2xl border border-gray-200 p-6">
                <p className="text-sm font-medium text-gray-900">Βάσεις εισαγωγής</p>
                <p className="mt-1 text-sm text-gray-600">
                  Από το 2018 μέχρι σήμερα, με ιστορικό, ΕΒΕ και εισακτέους ανά τμήμα.
                </p>
              </div>

              {/* Πλατύ κελί με τα μόρια ως αριθμό, στη γραμματοσειρά της εφαρμογής */}
              <div className="md:col-span-2 rounded-2xl border border-gray-200 p-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="max-w-sm">
                    <p className="text-sm font-medium text-gray-900">Δείχνει τι πιάνεις</p>
                    <p className="mt-1 text-sm text-gray-600">
                      Βάλε τους βαθμούς σου, πάρε τα μόριά σου, και δες μόνο τις σχολές που
                      φτάνουν εκεί.
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="pnl-numeral block text-5xl leading-none text-gray-900">
                      16.500
                    </span>
                    <span className="mt-1 block text-xs text-gray-500">μόρια, παράδειγμα</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3. ΠΟΤΕ ΜΙΛΑΕΙ: κάθετη ράγα, χωρίς κάρτες */}
        <section className="bg-gray-50 border-y border-gray-100">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
            <div className="max-w-2xl">
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">Πότε σου μιλάει</h2>
              <p className="mt-2 text-sm sm:text-base text-gray-600">
                Πέντε φορές συνολικά πριν το πρώτο μάθημα, και μία πριν από κάθε επόμενο. Τίποτα
                άλλο, ποτέ.
              </p>
            </div>

            <ol className="mt-8 max-w-2xl">
              {MILESTONES.map((milestone, index) => (
                <li key={milestone.days} className="flex gap-5">
                  {/* Η ράγα: ο αριθμός και η γραμμή που τους ενώνει */}
                  <div className="flex flex-col items-center">
                    <span className="pnl-numeral w-14 text-right text-2xl leading-none text-gray-900">
                      {milestone.days}
                    </span>
                    {index < MILESTONES.length - 1 ? (
                      <span className="my-2 w-px flex-1 bg-gray-200" aria-hidden="true" />
                    ) : null}
                  </div>

                  <div className={index < MILESTONES.length - 1 ? 'pb-8' : ''}>
                    <p className="text-sm font-medium text-gray-900">{milestone.title}</p>
                    <p className="mt-1 text-sm text-gray-600">{milestone.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* 4. ΤΙ ΔΕΝ ΚΑΝΕΙ: ένα πλάτος, μόνο κείμενο */}
        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">Τι δεν κάνει</h2>

              {/* Τα κουμπιά ανεβαίνουν δίπλα στον τίτλο: αλλιώς όλη η δεξιά
                  μισή σελίδα μένει άδεια. Ίδια διάταξη με το «Πώς λειτουργεί»
                  της αρχικής. */}
              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/vaseis/${MAX_YEAR}?ordering=-score`}
                  className="cursor-pointer inline-flex items-center justify-center rounded-xl bg-gray-900 px-5 py-3 text-sm font-medium text-white hover:bg-gray-800 transition"
                >
                  Δες Βάσεις
                </Link>
                <Link
                  to="/ypologismos-morion"
                  className="cursor-pointer inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 transition"
                >
                  Υπολόγισε μόρια
                </Link>
              </div>
            </div>

            <div className="mt-8 max-w-2xl">
              <p className="text-base text-gray-600">
                Δεν έχει λογαριασμό και δεν ζητάει email. Δεν έχει διαφημίσεις. Δεν συλλέγει
                δεδομένα και δεν στέλνει τίποτα πουθενά: το πεδίο σου, οι στόχοι σου και τα μόριά
                σου μένουν στο κινητό σου. Οι ειδοποιήσεις φτιάχνονται μέσα στη συσκευή, από
                ημερομηνίες που η εφαρμογή ήδη ξέρει.
              </p>
              <p className="mt-4 text-base text-gray-600">
                Μέχρι να βγει το επίσημο πρόγραμμα του Υπουργείου, οι ημερομηνίες είναι εκτίμηση με
                βάση το σταθερό μοτίβο των προηγούμενων ετών, και η εφαρμογή το γράφει καθαρά σε
                κάθε οθόνη. Μόλις βγει το ΦΕΚ, ενημερώνονται αυτόματα.
              </p>

              <p className="mt-4 text-base text-gray-600">
                Όσο περιμένεις, οι βάσεις και ο υπολογισμός μορίων δουλεύουν ήδη εδώ.
              </p>

              {/* Τα δύο URL που δηλώνονται στα stores. Πρέπει να είναι
                  προσβάσιμα από τη σελίδα της εφαρμογής, όχι μόνο να υπάρχουν:
                  ο reviewer τα ψάχνει, και έτσι τα βρίσκει και ο crawler. */}
              <p className="mt-6 text-sm text-gray-500">
                <Link to="/app/privacy" className="underline hover:text-gray-900 cursor-pointer">
                  Πολιτική απορρήτου
                </Link>
                <span aria-hidden="true"> · </span>
                <Link to="/app/support" className="underline hover:text-gray-900 cursor-pointer">
                  Υποστήριξη
                </Link>
              </p>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}
